import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { CONTRACT_TYPE } from '@avanta/shared/constants/psychologists';
import { LogIn } from 'lucide-react';
import { Activity } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { CancelAppointment } from 'components/CancelAppointment';
import { DownloadAppointmentPDF } from 'components/DownloadAppointmentPDF';
import { EditAppointment } from 'components/EditAppointment';
import { useIsPatientOrEmployee, useIsPsychologist, useUserStore } from 'stores/user';
import { type Appointment, AppointmentType } from 'types/appointments';
import { openInNewTab } from 'utils/common';
import { isDateTimePast } from 'utils/date';

const actionsContainerClassName = 'flex justify-end gap-4 max-am-xl:flex-col flex-wrap';
const buttonClassName = 'w-fit max-am-xl:w-full';

interface CardActionsProps {
  appointment: Appointment;
}

export const CardActions = ({ appointment }: CardActionsProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { state } = useLocation();
  const isPatientOrEmployee = useIsPatientOrEmployee();
  const isPsychologist = useIsPsychologist();
  const user = useUserStore(state => state.user);

  const from = state?.from;
  const isPsychologistEmployee = isPsychologist && user?.contractType === CONTRACT_TYPE.EMPLOYEE;
  const isRemoteAppointment = appointment.bookedContactMethod === AppointmentType.Video;
  const showEnterSessionButton =
    isRemoteAppointment && Boolean(appointment.sessionDetails?.onlineLink);
  const isAppointmentPast = isDateTimePast(appointment.dateTime, appointment.timeZone);
  const showEditAndCancelButtons =
    (!isPsychologistEmployee || isPatientOrEmployee) && !isAppointmentPast;
  const showActionsSection =
    showEnterSessionButton || isPatientOrEmployee || !isPsychologistEmployee;

  const handleOnEnterSession = (): void => {
    if (showEnterSessionButton && appointment.sessionDetails?.onlineLink) {
      openInNewTab(appointment.sessionDetails.onlineLink);
      return;
    }
  };

  return (
    <Activity mode={showActionsSection ? 'visible' : 'hidden'}>
      <div className={actionsContainerClassName}>
        {showEnterSessionButton && (
          <Button
            variant="secondary"
            className={buttonClassName}
            size="lg"
            onClick={handleOnEnterSession}
            icon="leading"
          >
            <LogIn className="size-4" />
            {t('AppointmentCard.enter.cta')}
          </Button>
        )}
        {isPatientOrEmployee && (
          <DownloadAppointmentPDF appointment={appointment} className={buttonClassName} />
        )}
        {showEditAndCancelButtons && (
          <>
            <EditAppointment appointment={appointment} from={from} className={buttonClassName} />
            <CancelAppointment appointment={appointment} className={buttonClassName} />
          </>
        )}
      </div>
    </Activity>
  );
};
